/**
 * Tender planning: what settles the check, and what goes back in change.
 *
 * Specification: ../test/tender.spec.ts. Master plan §4 M2, §4 M4, §4 M6, §8 P1.
 *
 * Pure, and deliberately so. `PosSession.tender()` has always taken a
 * `roundingMinor` from its caller and nothing decided it; this decides it, in
 * one place, so the till and the server cannot disagree about a haléř.
 *
 * CASH ROUNDS, CARD DOES NOT — rounding is a property of the TENDER, not of the
 * check. The same 234,50 bill settles at 235,00 in cash and at 234,50 on a
 * card, and a system that rounds the check gets both wrong the moment somebody
 * splits the payment, which in a Czech lunch trade is most of them.
 *
 * CHANGE IS NOT A TENDER. A 500 note against a 235,00 bill records a 235,00
 * tender and hands 265,00 back. Recording the note would put 265 crowns of
 * revenue on a check nobody bought and leave the drawer expecting money that
 * walked out in the guest's hand.
 *
 * The increment is DATA (D14). CZ withdrew the haléř and rounds to whole
 * koruna; the figure arrives from the regulatory registry rather than being
 * spelled into the till, so a second country is a configuration change.
 */

import { divRound } from "@gf/kernel/money";
import type { TenderKind } from "@gf/module-orders";

/**
 * Why a tender was refused, as a code rather than a sentence.
 *
 * The message here is for a log; the CODE is what the screen translates. A
 * cashier facing a queue needs "hotově jen celé koruny", not an English
 * sentence written by a developer — and D-plan's rule that no raw `Error` text
 * reaches a user only holds if the copy lives in the catalogue.
 */
export type TenderErrorCode =
  | "NOTHING_OUTSTANDING"
  | "NOT_WHOLE_KORUNA"
  | "NO_CHANGE_GIVEN"
  | "INVALID_AMOUNT"
  | "INVALID_TIP"
  | "INVALID_INCREMENT";

/** Every code, so the locale suite can prove all four languages cover them. */
export const TENDER_ERROR_CODES: readonly TenderErrorCode[] = [
  "NOTHING_OUTSTANDING",
  "NOT_WHOLE_KORUNA",
  "NO_CHANGE_GIVEN",
  "INVALID_AMOUNT",
  "INVALID_TIP",
  "INVALID_INCREMENT",
];

export class TenderError extends Error {
  readonly code: TenderErrorCode;

  constructor(code: TenderErrorCode, message: string) {
    super(message);
    this.name = "TenderError";
    this.code = code;
  }
}

/** CZ: whole koruna. See K11 — this is a registry value, not a constant. */
const DEFAULT_CASH_INCREMENT_MINOR = 100n;

export interface TenderPlan {
  readonly kind: TenderKind;
  /** What to record. Settles the check; never includes the tip or the change. */
  readonly amountMinor: bigint;
  /** Non-taxable. POSITIVE when the venue keeps the difference. */
  readonly roundingMinor: bigint;
  /** Handed back across the counter. Not a tender, not revenue. */
  readonly changeMinor: bigint;
  /** On top of the check: a liability to staff. */
  readonly tipMinor: bigint;
  /** Amount + tip. What the terminal is asked for, and the only place they meet. */
  readonly chargedMinor: bigint;
  readonly remainingMinor: bigint;
  readonly settles: boolean;
}

export interface PlanTenderArgs {
  /** `due + Σ rounding − Σ amount` for the check, before this tender. */
  readonly outstandingMinor: bigint;
  readonly kind: TenderKind;
  /** What was handed over or keyed. Omit to settle the balance exactly. */
  readonly offeredMinor?: bigint;
  readonly tipMinor?: bigint;
  readonly cashIncrementMinor?: bigint;
}

export function planTender(args: PlanTenderArgs): TenderPlan {
  const { outstandingMinor, kind } = args;
  const tipMinor = args.tipMinor ?? 0n;
  const increment = args.cashIncrementMinor ?? DEFAULT_CASH_INCREMENT_MINOR;

  if (outstandingMinor <= 0n) {
    throw new TenderError(
      "NOTHING_OUTSTANDING",
      `nothing is outstanding on this check (${outstandingMinor}); a further tender would ` +
        `over-settle it and it would never close`,
    );
  }
  if (tipMinor < 0n) {
    throw new TenderError("INVALID_TIP", `a tip cannot be negative (${tipMinor})`);
  }
  if (args.offeredMinor !== undefined && args.offeredMinor <= 0n) {
    throw new TenderError("INVALID_AMOUNT", `a tender of ${args.offeredMinor} is not a payment`);
  }
  if (increment <= 0n) {
    throw new TenderError("INVALID_INCREMENT", `cash increment must be positive (${increment})`);
  }

  if (kind === "CASH") {
    // Half away from zero, from the same shared implementation the server uses
    // (D1). A second rounding function on the till agrees for almost every
    // amount and disagrees on the ones that matter.
    const settleMinor = divRound(outstandingMinor, increment) * increment;

    if (args.offeredMinor !== undefined) {
      if (args.offeredMinor % increment !== 0n) {
        // You cannot hand over 8,73. The coins do not exist, so this is a typo
        // every time — and it would post a tender nobody can match in a drawer.
        throw new TenderError(
          "NOT_WHOLE_KORUNA",
          `${args.offeredMinor} cannot be handed over in cash: it is not a whole koruna. Cash ` +
            `moves in units of ${increment}.`,
        );
      }

      // Below the settling amount it is a part payment, not change owed. The
      // difference matters: the rest might arrive on a card, and rounding a
      // partial payment would round the check twice.
      if (args.offeredMinor < settleMinor) {
        const remainingMinor = outstandingMinor - args.offeredMinor;
        return {
          kind,
          amountMinor: args.offeredMinor,
          roundingMinor: 0n,
          changeMinor: 0n,
          tipMinor,
          chargedMinor: args.offeredMinor + tipMinor,
          remainingMinor,
          settles: false,
        };
      }
    }

    const roundingMinor = settleMinor - outstandingMinor;
    return {
      kind,
      amountMinor: settleMinor,
      roundingMinor,
      changeMinor: args.offeredMinor === undefined ? 0n : args.offeredMinor - settleMinor,
      tipMinor,
      chargedMinor: settleMinor + tipMinor,
      remainingMinor: outstandingMinor + roundingMinor - settleMinor,
      settles: true,
    };
  }

  const amountMinor = args.offeredMinor ?? outstandingMinor;
  if (amountMinor > outstandingMinor) {
    // No terminal and no voucher scheme gives change — `changeAllowed: false`
    // is the defining property of stravenky and the one an implementation
    // forgets. An amount above the balance is a mis-key, not a tip.
    throw new TenderError(
      "NO_CHANGE_GIVEN",
      `a ${kind} tender of ${amountMinor} exceeds the ${outstandingMinor} outstanding, and no ` +
        `card or voucher scheme gives change. Record the extra as a tip if that is what it is.`,
    );
  }

  const remainingMinor = outstandingMinor - amountMinor;
  return {
    kind,
    amountMinor,
    // Electronic money settles to the haléř, so there is nothing to round.
    roundingMinor: 0n,
    changeMinor: 0n,
    tipMinor,
    chargedMinor: amountMinor + tipMinor,
    remainingMinor,
    settles: remainingMinor === 0n,
  };
}

export type TenderAttempt =
  | { readonly ok: true; readonly plan: TenderPlan }
  | { readonly ok: false; readonly code: TenderErrorCode };

/**
 * The same decision, without throwing.
 *
 * The screen has to PREVIEW a tender — the cashier must see the rounding and
 * the change before the drawer opens, not after — and a preview that throws
 * on every half-typed amount is a preview nobody can build. Same function, one
 * return shape, so the number shown and the number recorded cannot drift.
 */
export function tryPlanTender(args: PlanTenderArgs): TenderAttempt {
  try {
    return { ok: true, plan: planTender(args) };
  } catch (error) {
    if (error instanceof TenderError) return { ok: false, code: error.code };
    throw error;
  }
}
